import {
  ITextOverlayV2,
  validateOverlayV2Integrity,
} from 'blocks/TextOverlayModal/v2';
import { DynamicElementType } from 'components/VideoTemplateEditor/types';
import { DeepImmutableMap } from 'types';
import { DYNAMIC_ELEMENTS } from 'utils/constants';
import { textContentFromHtmlString } from 'utils/dom';
import { scaleInlineStyles } from 'utils/embed/text-overlay';
import { Pixels, ViewportWidth } from 'utils/measurement';
import { TextOverlay } from '../../types';
import { PADDING_KEYS } from './constants';
import { PaddingFormatter, PaddingValues } from './types';

export function formatPadding<T extends PaddingValues<U>, U, V>(
  t: T,
  formatter: PaddingFormatter<U, V>,
): PaddingValues<V> {
  return PADDING_KEYS.reduce((acc, key) => {
    acc[key] = formatter(t[key], key);
    return acc;
  }, {} as any);
}

/*
 * take an overlay output from the TextOverlay modal and transform it to the
 * shape needed for VideoTemplateEditorState.  Shape is the same but values are
 * slightly different in order to remove assumptions about untis (px, vw, etc.)
 */
export function overlayToState(
  overlay: DeepImmutableMap<ITextOverlayV2>,
): Omit<TextOverlay, 'id' | 'layerId' | 'integrationData'> {
  const measurementContext = overlay.get('viewport').toJS();
  const style = overlay.get('style').toJS();
  const editor = overlay.get('editor')?.toJS?.();
  const textBuilderStyles = overlay.get('textBuilderStyles')?.toJS?.();

  const toVw = (val: number) =>
    new Pixels(val).toUnit('vw', measurementContext);
  const toVh = (val: number) =>
    new Pixels(val).toUnit('vh', measurementContext);

  return {
    advancedTextConfigs: overlay.get('advancedTextConfigs')?.toJS(),
    editor,
    position: {
      left: toVw(overlay.getIn(['position', 'left'])),
      top: toVh(overlay.getIn(['position', 'top'])),
    },
    size: {
      height: toVh(overlay.getIn(['size', 'height'])),
      width: toVw(overlay.getIn(['size', 'width'])),
    },
    style: {
      ...style,
      fontSize: toVw(style.fontSize),
      ...formatPadding(style, (vw: number) => new ViewportWidth(vw)),
    },
    text: validateOverlayV2Integrity(overlay)
      ? overlay.get('text')
      : textContentFromHtmlString(overlay.get('textHtml') || ''),
    textBuilderStyles,
    textHtml: scaleInlineStyles(overlay.get('textHtml'), ['fontSize'], val =>
      toVw(val).toString(),
    ),
    version: overlay.get('version'),
  };
}

export function checkIsDynamicElement(
  element: string,
): element is DynamicElementType {
  return DYNAMIC_ELEMENTS.includes(element);
}
